import React, { useEffect, useState, useMemo } from 'react'
import {
  Table,
  message,
  Typography,
  Tag,
  Space,
  Button,
  Popconfirm,
  Input,
  Switch,
  Modal,
  Form,
  Input as AntInput,
  Card
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  StopOutlined,
  EditOutlined
} from '@ant-design/icons'
import api from '@/services/api'
import { MoreOutlined } from '@ant-design/icons'
import { Dropdown, Menu, Tooltip } from 'antd'

const { Title } = Typography

const ManagementUsers = () => {
  const [loadingFetch, setLoadingFetch] = useState(false)
  const [relations, setRelations] = useState([])
  const [searchText, setSearchText] = useState('')
  const [isModalVisible, setIsModalVisible] = useState(false)  // State for modal visibility
  const [currentRelationId, setCurrentRelationId] = useState(null)  // State to store current Relation ID for the new user
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)  // State for edit modal visibility
  const [currentUserData, setCurrentUserData] = useState(null)  // Store current user data for editing
  const [form] = Form.useForm()
  const [loadingCreate, setLoadingCreate] = useState(false)
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [loadingToggle, setLoadingToggle] = useState(false)
  const [selectedToggleUserId, setSelectedToggleUserId] = useState(null)

  const fetchUsers = async () => {
    setLoadingFetch(true)
    try {
      const response = await api()({
        url: '/webshop/management/user',
        method: 'get'
      })


      setRelations(response.data)
      message.success('Users fetched successfully')
    } catch (error) {
      console.error(error)
      message.error('Failed to fetch users')
    } finally {
      setLoadingFetch(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleCreateUser = relationId => {
    form.resetFields()  // Reset form fields
    setCurrentRelationId(relationId)  // Set current relation ID to use in the form
    setIsModalVisible(true)  // Show modal
  }

  const handleEdit = userId => {
    const user = relations
      .flatMap(relation => relation.users)
      .find(user => user.Id === userId)
    setCurrentUserData(user)  // Set the current user data for editing
    form.setFieldsValue(user)  // Pre-fill the form with the user's data
    setIsEditModalVisible(true)  // Show the edit modal
  }

  const handleDeleteUser = userId => {
    message.warning(`Kullanıcı silindi: ID ${userId}`)
    // Silme işlemi API üzerinden yapılabilir
  }

  const handleBlockUser = (userId, block) => {

    // Block işlemi API üzerinden yapılabilir
    api()({
      url: `/webshop/management/user/block/${userId}?block=${block ? 0 : 1}`,
      method: 'patch'
    })
      .then(() => {
        message.success(`User blocked successfully`)
        fetchUsers()
      }).catch(() => {
        message.error('Failed to block user')
      })
  }


  const handleLogout = userId => {
    message.info(`Kullanıcı oturumu kapatıldı: ID ${userId}`)
    // Logout işlemi API üzerinden yapılabilir
    api()({
      url: `/webshop/management/user/logout/${userId}`,
      method: 'patch'
    })
      .then(() => {
        message.success(`User blocked successfully`)
        fetchUsers()
      })
      .catch(() => {
        message.error('Failed to block user')
      })
  }

  const handleToggleAdmin = (userId, currentRole) => {
    setLoadingToggle(true)
    setSelectedToggleUserId(userId)
    const newRole = currentRole === 1 ? 0 : 1
    api()({
      url: `/webshop/management/user/${userId}/role`,
      method: 'put',
      data: { role: newRole }
    })
      .then(() => {
        message.success(`User role updated successfully`)
        setRelations(prevRelations =>
          prevRelations.map(relation => ({
            ...relation,
            users: relation.users.map(user =>
              user.Id === userId ? { ...user, Role: newRole } : user
            )
          }))
        )
      })
      .catch(() => {
        message.error('Failed to update user role')
      }).finally(() => {
        setLoadingToggle(false)
        setSelectedToggleUserId(null)
      })
  }

  const handleSubmit = async (values) => {
    setLoadingCreate(true)
    try {
      await api()({
        url: '/webshop/management/user',
        method: 'post',
        data: {
          ...values,
          RelationId: currentRelationId
        },
      })
      message.success('New user created successfully')
      setIsModalVisible(false)
      fetchUsers()
    } catch (error) {
      message.error(error?.response?.data?.message || 'Failed to create user')
    } finally {
      setLoadingCreate(false)
    }
  }

  const handleEditSubmit = async (values) => {
    setLoadingEdit(true)
    try {
      await api()({
        url: `/webshop/management/user/${currentUserData.Id}`,
        method: 'put',
        data: values
      })
      message.success('User updated successfully')
      setIsEditModalVisible(false)
      fetchUsers()
    } catch (error) {
      message.error(error?.response?.data?.message || 'Failed to update user')
    } finally {
      setLoadingEdit(false)
    }
  }

  const filteredRelations = useMemo(() => {
    if (!searchText) return relations
    return relations.filter(r => {
      return (
        r.Name?.toLowerCase().includes(searchText) ||
        r.Email?.toLowerCase().includes(searchText) ||
        r.RelationId?.toString().includes(searchText) ||
        r.ShortName?.toLowerCase().includes(searchText) ||
        r.Phone?.toLowerCase().includes(searchText)
      )
    })
  }, [searchText, relations])

  const relationColumns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'Name'
    },
    {
      title: 'Short Name',
      dataIndex: 'ShortName',
      key: 'ShortName'
    },
    {
      title: 'Relation ID',
      dataIndex: 'RelationId',
      key: 'RelationId'
    },
    {
      title: 'Customer Type',
      dataIndex: 'CustomerType',
      key: 'CustomerType',
      render: type => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Phone',
      dataIndex: 'Phone',
      key: 'Phone'
    },

    {
      title: '',
      key: 'actions',
      render: (_, record) => {
        const userCount = record.users?.length || 0
        const hasUsers = userCount > 0

        return (
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleCreateUser(record.RelationId)}
            >
              New User
            </Button>
            <Tag color={hasUsers ? 'green' : 'default'}>
              {hasUsers
                ? `${userCount} User${userCount > 1 ? 's' : ''}`
                : 'No Users'}
            </Tag>
          </Space>
        )
      }
    }
  ]

  const userColumns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'Name'
    },
    {
      title: 'First Name',
      dataIndex: 'FirstName',
      key: 'FirstName'
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      render: email => <a href={`mailto:${email}`}>{email}</a>
    },
    {
      title: 'Last Seen',
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      render: lastSeen =>
        lastSeen ? new Date(lastSeen).toLocaleString('tr-TR') : 'Bilinmiyor'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, user) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="edit"
              icon={<EditOutlined />}
              onClick={() => handleEdit(user.Id)}
            >
              Düzenle
            </Menu.Item>
            <Menu.Item
              key="logout"
              icon={<StopOutlined />}
              disabled={user.Role == 1} // Disable if user is admin
              onClick={() => handleLogout(user.Id)}
            >
              Logout
            </Menu.Item>
            <Menu.Item
              key="block"
              icon={<StopOutlined />}
              disabled={user.Role == 1} // Disable if user is admin
              onClick={() => handleBlockUser(user.Id, user.IsBlocked)}
            >
              {user?.IsBlocked ? 'Engeli Kaldır' : 'Engelle'}
            </Menu.Item>
          </Menu>
        )

        return (
          <Space>
            <Tooltip title="Sil">
              <Popconfirm
                title="Bu kullanıcıyı silmek istediğinizden emin misiniz?"
                onConfirm={() => handleDeleteUser(user.Id)}
                okText="Evet"
                cancelText="İptal"
              >
                <Button icon={<DeleteOutlined />} danger size="small" />
              </Popconfirm>
            </Tooltip>

            <Tooltip title="Rol Değiştir">
              <Switch
                size="small"
                loading={loadingToggle && selectedToggleUserId === user.Id}
                checked={user.Role === 1}
                onChange={() => handleToggleAdmin(user.Id, user.Role)}
                checkedChildren="Admin"
                unCheckedChildren="User"
              />
            </Tooltip>

            <Dropdown overlay={menu} trigger={['click']}>
              <Button icon={<MoreOutlined />} size="small" />
            </Dropdown>
          </Space>
        )
      }
    }
  ]



  return (
    <Card>
      <Title level={3}>Relations Management</Title>
      <Input
        placeholder="Relation adı, email, ID ara..."
        enterButton="Ara"
        onChange={e => setSearchText(e.target.value.toLowerCase())}
      />



      <br />
      <Table
        dataSource={filteredRelations}
        columns={relationColumns}
        rowKey="Id"
        loading={loadingFetch}
        expandable={{
          expandedRowRender: record => {
            if (!record.users || record.users.length === 0) {
              return <p style={{ margin: 0 }}>Bu relation'a ait kullanıcı yok.</p>
            }
            return (
              <Table
                dataSource={record.users}
                columns={userColumns}
                rowKey="Id"
                pagination={false}
                size="small"
              />
            )
          },
          rowExpandable: record => record.users && record.users.length > 0
        }}
        pagination={{ pageSize: 10 }}
        bordered
      />

      {/* Modal for creating new user */}
      <Modal
        title="Create New User"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout='vertical'
        >
          <Form.Item
            name="Name"
            label="Name"
            rules={[{ required: true, message: 'Please input the user name!' }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="FirstName"
            label="First Name"
            rules={[{ required: true, message: 'Please input the first name!' }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="Email"
            label="Email"
            rules={[{ required: true, message: 'Please input the email!' }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="Password"
            label="Password"
            rules={[{ required: true, message: 'Please input the password!' }]}
          >
            <AntInput.Password />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loadingCreate}>Create User</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for editing user */}
      <Modal
        title="Edit User"
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          onFinish={handleEditSubmit}
          layout='vertical'
        >
          <Form.Item
            name="Name"
            label="Name"
            rules={[{ required: true, message: 'Please input the user name!' }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="FirstName"
            label="First Name"
            rules={[{ required: true, message: 'Please input the first name!' }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="Email"
            label="Email"
            rules={[{ required: true, message: 'Please input the email!' }]}
          >
            <AntInput />
          </Form.Item>
          <Form.Item
            name="Password"
            label="Password"
          >
            <AntInput.Password />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loadingEdit}>Update User</Button>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}

export default ManagementUsers
